import React, { useEffect, useState, useCallback } from "react";
import { Section, Eyebrow, Heading } from "../components/site/Section";
import { adminSiteStats, getAdminKey, setAdminKey, clearAdminKey,
  adminTriviaCompletions, adminSnapshotSuperfan, adminGetSuperfanSnapshot } from "../lib/archive";
import { NATIONS_LIST } from "../lib/archive";
import { useAuth } from "../lib/auth";
import { RefreshCw, LogOut, Trophy, Camera } from "lucide-react";
import { toast } from "sonner";

export default function AdminStatsPage() {
  const { user } = useAuth();
  const isOwner = !!user?.is_owner;
  const [keyInput, setKeyInput] = useState("");
  // Auto-authenticated if either: static admin key stored, or logged-in as owner
  const [hasKey, setHasKey] = useState(() => !!getAdminKey() || isOwner);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Re-check when auth resolves after mount (user may load async).
  useEffect(() => {
    if (isOwner) setHasKey(true);
  }, [isOwner]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const s = await adminSiteStats();
      setStats(s);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 || status === 403) {
        clearAdminKey();
        setHasKey(false);
        setError("Admin key rejected.");
      } else {
        setError("Failed to load stats.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasKey) load();
  }, [hasKey, load]);

  const submitKey = (e) => {
    e.preventDefault();
    if (!keyInput.trim()) return;
    setAdminKey(keyInput.trim());
    setHasKey(true);
    setKeyInput("");
  };

  const signOut = () => {
    clearAdminKey();
    // If owner is logged in, they'll still auto-auth; only fully clear if not owner.
    setHasKey(isOwner);
    if (!isOwner) setStats(null);
  };

  if (!hasKey) {
    return (
      <div data-testid="admin-stats-page">
        <Section className="pt-24 pb-16 max-w-md">
          <Eyebrow>Admin</Eyebrow>
          <Heading>Site Stats</Heading>
          <form onSubmit={submitKey} className="mt-8 space-y-3">
            <input
              type="password"
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="Admin key"
              data-testid="admin-stats-key-input"
              className="w-full bg-[#0a0a0a] border border-white/10 focus:border-[#C5A059] px-4 py-3 text-[#EAE0D5] outline-none"
            />
            <button
              type="submit"
              data-testid="admin-stats-key-submit"
              className="w-full bg-[#C5A059] text-black px-4 py-3 text-[11px] tracking-[0.22em] uppercase font-semibold hover:bg-[#D4AF68] transition-colors"
            >
              Unlock
            </button>
            {error && <p className="text-[#B85C5C] text-sm">{error}</p>}
          </form>
        </Section>
      </div>
    );
  }

  return (
    <div data-testid="admin-stats-page">
      <Section className="pt-24 pb-16">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <Eyebrow>Admin · Live</Eyebrow>
            <Heading>Site Stats</Heading>
          </div>
          <div className="flex items-center gap-2 mt-2">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-stats-refresh"
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#C5A059]/60 hover:text-[#C5A059] text-[#A39E99] px-3 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
            <button
              type="button"
              onClick={signOut}
              data-testid="admin-stats-signout"
              className="inline-flex items-center gap-1.5 border border-white/15 hover:border-[#B85C5C]/60 hover:text-[#B85C5C] text-[#A39E99] px-3 py-2 text-[10px] tracking-[0.22em] uppercase transition-colors"
            >
              <LogOut size={12} /> Sign out
            </button>
          </div>
        </div>

        {error && <p className="mt-6 text-[#B85C5C] text-sm" data-testid="admin-stats-error">{error}</p>}
        {!stats && !error && <p className="mt-8 text-[#A39E99] text-sm">Loading…</p>}

        {stats && (
          <div className="mt-10 space-y-10">
            <TopRow stats={stats} />
            <NationTable
              title="Drop pulls per nation"
              subtitle="Every character drop the archive has served (weekly + convention)."
              rows={NATIONS_LIST.map((n) => ({
                nation: n,
                count: stats.pulls_by_nation?.[n.slug] || 0,
              })).sort((a, b) => b.count - a.count)}
              testIdPrefix="pull"
            />
            <NationTable
              title="Quiz results by nation"
              subtitle={`Which nation the quiz assigned each keeper (${stats.quiz?.completions ?? 0} completion${(stats.quiz?.completions ?? 0) === 1 ? "" : "s"}, includes anonymous).`}
              rows={NATIONS_LIST.map((n) => ({
                nation: n,
                count: stats.quiz?.pulls_by_nation?.[n.slug] || 0,
              })).sort((a, b) => b.count - a.count)}
              testIdPrefix="quiz-nation"
            />
            <NationTable
              title="Users by claimed nation"
              subtitle={`Where the ${stats.users?.non_owner ?? 0} signed-in keepers pledged their loyalty${stats.users?.unclaimed ? ` (+${stats.users.unclaimed} without a nation yet)` : ""}.`}
              rows={NATIONS_LIST.map((n) => ({
                nation: n,
                count: stats.users?.by_nation?.[n.slug] || 0,
              })).sort((a, b) => b.count - a.count)}
              testIdPrefix="claim"
            />
            <NationTable
              title="Guess accuracy by target nation"
              subtitle={`${stats.guesses?.correct ?? 0}/${stats.guesses?.total ?? 0} total guesses correct.`}
              rows={NATIONS_LIST.map((n) => {
                const g = stats.guesses?.by_nation?.[n.slug] || { total: 0, correct: 0 };
                return { nation: n, count: g.total, correct: g.correct };
              }).sort((a, b) => b.count - a.count)}
              testIdPrefix="guess"
              showAccuracy
            />
            <SuperfanSection />
          </div>
        )}
      </Section>
    </div>
  );
}

function SuperfanSection() {
  const [completions, setCompletions] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [snapBusy, setSnapBusy] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([
        adminTriviaCompletions(),
        adminGetSuperfanSnapshot(),
      ]);
      setCompletions(c);
      setSnapshot(s?.snapshot || null);
    } catch { /* silent */ }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const takeSnapshot = async () => {
    if (!window.confirm("Snapshot the current #1 leaderboard keeper as the Anime NYC Super Fan?")) return;
    setSnapBusy(true);
    try {
      const s = await adminSnapshotSuperfan();
      setSnapshot(s);
      toast.success(`Super Fan locked: ${s.winner_username || s.winner_email}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Snapshot failed.");
    } finally {
      setSnapBusy(false);
    }
  };

  return (
    <div data-testid="admin-superfan-section">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display text-2xl text-[#EAE0D5] flex items-center gap-2"><Trophy size={18} className="text-[#C5A059]" /> Super Fan</h3>
        <button
          type="button"
          onClick={takeSnapshot}
          disabled={snapBusy}
          data-testid="admin-superfan-snapshot"
          className="inline-flex items-center gap-1.5 border border-[#C5A059]/40 hover:bg-[#C5A059]/10 text-[#C5A059] px-3 py-2 text-[10px] tracking-[0.22em] uppercase disabled:opacity-40"
        >
          <Camera size={12} /> {snapBusy ? "Locking…" : "Snapshot ANYC Super Fan"}
        </button>
      </div>

      <div className="mt-4 grid md:grid-cols-2 gap-4">
        {/* Snapshot */}
        <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="admin-superfan-snapshot-card">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#A39E99]">ANYC Super Fan (leaderboard snapshot)</div>
          {snapshot ? (
            <>
              <div className="mt-2 font-display text-2xl text-[#EAE0D5]">{snapshot.winner_username || snapshot.winner_email || "—"}</div>
              <div className="mt-1 text-xs text-[#A39E99]">{snapshot.unlocks} unlocks · frozen {new Date(snapshot.snapshotted_at).toLocaleString()}</div>
              {snapshot.winner_email && (
                <div className="mt-1 text-xs text-[#737373] truncate">{snapshot.winner_email}</div>
              )}
            </>
          ) : (
            <p className="mt-3 text-sm text-[#737373]">Not snapshotted yet. Tap the button at Sun 4 PM ET at the booth.</p>
          )}
        </div>

        {/* Trivia completions */}
        <div className="bg-[#0a0a0a] border border-white/5 p-5" data-testid="admin-trivia-completions-card">
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#A39E99]">Trivia completions ({completions?.total ?? 0})</div>
          {(completions?.completions?.length || 0) === 0 ? (
            <p className="mt-3 text-sm text-[#737373]">Nobody&rsquo;s cracked all five yet.</p>
          ) : (
            <ul className="mt-3 divide-y divide-white/5 max-h-64 overflow-y-auto">
              {completions.completions.map((c) => (
                <li key={c.user_id} className="py-2 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-[#EAE0D5] truncate">{c.username || c.email || c.user_id}</div>
                    {c.email && <div className="text-xs text-[#A39E99] truncate">{c.email}</div>}
                    <div className="text-[10px] tracking-[0.18em] uppercase text-[#737373] mt-0.5">
                      {new Date(c.passed_at).toLocaleString()}
                    </div>
                  </div>
                  {c.is_first_winner && (
                    <span className="shrink-0 text-[10px] tracking-[0.24em] uppercase border border-[#C5A059]/50 text-[#C5A059] px-2 py-1">
                      First
                    </span>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

function TopRow({ stats }) {
  const quizAnon = stats.quiz?.anonymous ?? 0;
  const quizSigned = stats.quiz?.signed_in ?? 0;
  const quizLabel = quizAnon > 0
    ? `${stats.quiz?.completions ?? 0}`
    : `${stats.quiz?.completions ?? 0}`;
  const quizSub = quizAnon > 0 || quizSigned > 0
    ? `${quizAnon} anon · ${quizSigned} signed-in`
    : null;
  const tiles = [
    { label: "Total keepers", value: stats.users?.non_owner ?? 0, testId: "stat-total-users" },
    { label: "Total logins", value: stats.logins?.total ?? 0, testId: "stat-total-logins" },
    { label: "Quiz completions", value: quizLabel, sub: quizSub, testId: "stat-quiz-completions" },
    { label: "Total pulls", value: Object.values(stats.pulls_by_nation || {}).reduce((a, b) => a + b, 0), testId: "stat-total-pulls" },
    { label: "Total guesses", value: stats.guesses?.total ?? 0, testId: "stat-total-guesses" },
    { label: "Records handed out", value: stats.unlocks?.personal_total ?? 0, testId: "stat-total-unlocks" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
      {tiles.map((t) => (
        <div key={t.label} data-testid={t.testId} className="bg-[#0a0a0a] border border-white/10 p-5">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">{t.label}</div>
          <div className="mt-2 font-display text-3xl md:text-4xl text-[#EAE0D5]">{t.value}</div>
          {t.sub && (
            <div className="mt-1 text-[10px] tracking-[0.18em] uppercase text-[#737373]">{t.sub}</div>
          )}
        </div>
      ))}
    </div>
  );
}

function NationTable({ title, subtitle, rows, testIdPrefix, showAccuracy = false }) {
  const max = Math.max(1, ...rows.map((r) => r.count));
  return (
    <div>
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <h3 className="font-display text-2xl text-[#EAE0D5]">{title}</h3>
        <p className="text-xs text-[#A39E99]">{subtitle}</p>
      </div>
      <div className="mt-4 divide-y divide-white/5 border border-white/5 bg-[#0a0a0a]">
        {rows.map((r) => {
          const pct = Math.round((r.count / max) * 100);
          const acc = showAccuracy && r.count > 0 ? Math.round(((r.correct || 0) / r.count) * 100) : null;
          return (
            <div
              key={r.nation.slug}
              className="grid grid-cols-[110px_1fr_auto] items-center gap-3 px-4 py-3"
              data-testid={`${testIdPrefix}-row-${r.nation.slug}`}
            >
              <div className="text-[10px] tracking-[0.24em] uppercase text-[#EAE0D5]">
                {r.nation.name}
              </div>
              <div className="relative h-2 bg-white/5 overflow-hidden">
                <div
                  className="absolute inset-y-0 left-0 transition-[width] duration-500"
                  style={{ width: `${pct}%`, background: r.nation.accent || "#C5A059" }}
                />
              </div>
              <div className="text-[#A39E99] text-sm text-right min-w-[90px]">
                <span className="text-[#EAE0D5] font-semibold" data-testid={`${testIdPrefix}-count-${r.nation.slug}`}>{r.count}</span>
                {acc !== null && <span className="ml-2 text-xs">{acc}% acc</span>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
